import AbstractProvider from '../services/App/AbstractProvider';
import {NotificationTypes} from '../@interfaces/models/Notification/INotificationModel';
import {IUser} from '../@interfaces/services/Auth/IUser';
import {newNotificationCreated} from '@src/_redux/notifications/actions';
import {IEvent, IEventDefaultPayload, IListener, ISubscription} from '@src/@interfaces/services/Events';
import {fetchUserModel} from '@src/_redux/user/actions';
import {NonNull} from '@src/helpers/ts/notNull';
import app from '@src/helpers/app';
import {IConfig} from '@src/@interfaces/services/Config/IConfig';
import {TBaseWSMessage} from '@src/@types/hooks/useWebSocket';

export class EventsListenersProvider extends AbstractProvider {
    protected refreshUserDetailsTimer: ReturnType<typeof setTimeout> | null = null;

    static NOTIFICATION_SOUNDS = {
        NEW_ORDER: 'new-order',
        NEW_ORDER_PRODUCT: 'new-order-product',
        NEW_ORDER_COMMENT: 'new-order-comment',
        NEW_ORDER_PRODUCT_COMMENT: 'new-order-product-comment',
    } as const;

    constructor(app: any) {
        super(app);

        this.onNewNotificationReceived = this.onNewNotificationReceived.bind(this);
    }

    async boot() {
        let prevUser: IUser | null = null;
        let subscriptions: ISubscription[] = [];

        this.app.make('auth').onUserDetailsChanged((event, {user}) => {
            const prevId = prevUser?.id;
            const userIdChanged = prevId !== user?.id;

            // unsubscribe from previous events
            subscriptions.forEach(item => item.unsubscribe());
            subscriptions = [];

            const ws = this.app.make('ws');
            if (userIdChanged && prevId) {
                // user id has been changed, we have to leave previous user channel
                ws.leave(`model.user.${prevId}`);
            }

            prevUser = user;
            if (!user) {
                ws.leave('model.user-role');

                // this is logout
                return;
            }

            const channelName = `model.user.${user.id}`;
            if (userIdChanged) {
                // user id has been changed, we have to listen user channel for notification
                ws.private(channelName).notification(this.onNewNotificationReceived);
            }

            // subscribe for others events
            const onUserOrTheirRoleChanged = (event: IEvent, payload?: TBaseWSMessage | null | undefined): void => {
                if (
                    !this.isNotificationEvent(event) &&
                    payload != null &&
                    typeof payload === 'object' &&
                    !Array.isArray(payload) &&
                    ['App\\Models\\User', 'App\\Models\\UserRole'].includes(payload.model)
                ) {
                    this.onUserOrTheirRoleChanged();
                }
            };

            [
                ws.onPrivateMessage(channelName, '*', onUserOrTheirRoleChanged),
                ws.onPrivateMessage('model.user-role', '*', (event, payload: TBaseWSMessage) => {
                    if ([user.user_role_id, user.role?.parent_id].filter(NonNull).includes(payload.id)) {
                        onUserOrTheirRoleChanged(event, payload);
                    }
                }),
                this.listenEventsOfOrderModelChannel(),
                this.listenEventsOfOrderProductModelChannel(),
            ].forEach(subscription => {
                if (subscription) {
                    subscriptions.push(subscription);
                }
            });
        });
    }

    protected onNewNotificationReceived(notification: { type: NotificationTypes }) {
        this.app.dispatchAction(newNotificationCreated());

        if (notification.type === 'App\\Notifications\\Order\\CommentAdded') {
            this.playNewOrderCommentSound();
        } else if (notification.type === 'App\\Notifications\\Order\\Product\\CommentAdded') {
            this.playNewOrderProductCommentSound();
        }
    }

    protected onUserOrTheirRoleChanged() {
        if (this.refreshUserDetailsTimer) {
            clearTimeout(this.refreshUserDetailsTimer);
        }

        this.refreshUserDetailsTimer = setTimeout(() => {
            this.app.dispatchAction(fetchUserModel());
        }, 1500);
    }

    protected isNotificationEvent(event: IEvent): boolean {
        return event.name.includes('Illuminate\\Notifications\\Events\\BroadcastNotificationCreated');
    }

    protected listenEventsOfOrderModelChannel(): ISubscription | null {
        return this.listenEventsOfModelChannel(
            'model.order',
            ['OrderCreated', 'OrderUpdated'],
            () => {
                this.playNewOrderSound();
            },
            this.notificationTypes.NEW_ORDER,
            ['users@is-manager', 'manager-dashboard@can-see-dashboard'],
        );
    }

    protected listenEventsOfOrderProductModelChannel(): ISubscription | null {
        return this.listenEventsOfModelChannel<{ department_id: string | number }>(
            'model.order-product',
            ['OrderProductCreated', 'OrderProductUpdated'],
            (event, {user, department_id}) => {
                const departmentId = +department_id;
                const userDepartments = (user.departments ?? []).map(item => item.id ?? 0);
                const listenOnlyUserDepartments = this.config.get(
                    'settings.notifications.new-order-product.only-my-departments',
                    true
                );
                const specialDepartmentsToListen = this.config.get<(string | number)[]>(
                    'settings.notifications.new-order-product.only-these-departments',
                    []
                );

                const departmentsToListen = (
                    listenOnlyUserDepartments ?
                        userDepartments :
                        specialDepartmentsToListen
                ).map(id => +id);

                if (departmentsToListen.includes(departmentId)) {
                    this.playNewOrderProductSound();
                }
            },
            this.notificationTypes.NEW_ORDER_PRODUCT,
            [
                'users@is-manager', 'manager-dashboard@can-see-dashboard',
                'users@is-production', 'production-dashboard@can-see-dashboard',
            ],
        );
    }

    protected listenEventsOfModelChannel<TListenerPayload>(
        channelName: string | string[],
        events: string | string[],
        listener: IListener<TListenerPayload & IEventDefaultPayload & {
            user: IUser,
            status_id: string | number
        }>,
        notificationType: typeof this.notificationTypes['NEW_ORDER'] | typeof this.notificationTypes['NEW_ORDER_PRODUCT'],
        permissions?: string | string[],
    ): ISubscription | null {
        const ws = this.app.make('ws');
        const user = this.app.make('auth').getUser();
        if (!user || permissions != null && !user.canAny(permissions)) {
            return null;
        }

        const enabled = this.isThisSoundEnabled(notificationType);

        const listenChannel = () => ws.onPrivateMessage<TListenerPayload & IEventDefaultPayload & {
            user: IUser,
            status_id: string | number
        }>(
            channelName,
            events,
            (event, payload) => {
                const statusesToListen = this.config.get<(number | string)[]>(
                    `settings.notifications.${notificationType}.statuses`,
                    []
                ).map(id => +id);

                if (
                    statusesToListen.includes(+payload.status_id) &&
                    (!payload.performed_by || payload.performed_by !== user.id)
                ) {
                    listener(event, {...payload, user});
                }
            }
        );

        let subscription: ISubscription | null = null;
        if (enabled) {
            subscription = listenChannel();
        } else {
            subscription = this.config.onValueChanged(
                `settings.notifications.${notificationType}.sound.play`,
                (event, {value}) => {
                    if (value) {
                        subscription?.unsubscribe();
                        subscription = listenChannel();
                    }
                }
            );
        }

        return {
            unsubscribe() {
                subscription?.unsubscribe();
                subscription = null;

                (Array.isArray(channelName) ? channelName : [channelName]).forEach((channelName) => {
                    ws.leave(channelName);
                });
            }
        };
    }

    protected playNewOrderSound(): void {
        this.playSoundIfItEnabled(this.notificationTypes.NEW_ORDER);
    }

    protected playNewOrderProductSound(): void {
        this.playSoundIfItEnabled(this.notificationTypes.NEW_ORDER_PRODUCT);
    }

    protected playNewOrderCommentSound(): void {
        this.playSoundIfItEnabled(this.notificationTypes.NEW_ORDER_COMMENT);
    }

    protected playNewOrderProductCommentSound(): void {
        this.playSoundIfItEnabled(this.notificationTypes.NEW_ORDER_PRODUCT_COMMENT);
    }

    protected isThisSoundEnabled(soundName: typeof this.notificationTypes[keyof typeof this.notificationTypes]): boolean {
        return this.config.get(`settings.notifications.${soundName}.sound.play`, true);
    }

    protected playSoundIfItEnabled(soundName: typeof this.notificationTypes[keyof typeof this.notificationTypes]): void {
        if (this.isThisSoundEnabled(soundName)) {
            this.player.play(soundName).then();
        }
    }

    protected get player() {
        return app('sounds-player');
    }

    protected get config() {
        return this.app.make('config') as IConfig;
    }

    protected get notificationTypes() {
        return EventsListenersProvider.NOTIFICATION_SOUNDS;
    }
}
